import type { QueryClient } from '@tanstack/react-query';

import * as Sentry from '@sentry/react';

import { API } from '@/shared/api';
import { MixpanelEvents, setMixpanelUserPropertiesOnce } from '@/shared/thirdParty/mixpanel';
import { User } from '@/shared/types/User';

import type { TrialVariant } from '../lib/types';

import { computeTrialVariant } from '../lib/computeTrialVariant';
import { TRIAL_VARIANT_KEY } from '../lib/constants';
import { isExcludedFromTrialVariant } from '../lib/eligibility';
import { persistTrialVariant } from './persistTrialVariant';
import { useTrialVariantStore } from './useTrialVariantStore';

type AssignTrialVariantParams = {
  queryClient: QueryClient;
  user: Pick<User, 'country' | 'id'>;
};

const resolveCountry = async (user: AssignTrialVariantParams['user']) => {
  if (user.country) return user.country;

  try {
    return (await API.getUser()).country ?? null;
  } catch {
    return null;
  }
};

export const assignTrialVariant = async ({ queryClient, user }: AssignTrialVariantParams) => {
  if (isExcludedFromTrialVariant()) return null;

  try {
    const country = await resolveCountry(user);
    const { setAssignment } = useTrialVariantStore.getState();
    const variant: TrialVariant = computeTrialVariant(user.id, country);

    setAssignment({ userId: user.id, variant });

    MixpanelEvents.variant_trial({ variant });
    setMixpanelUserPropertiesOnce({ [TRIAL_VARIANT_KEY]: variant });

    void persistTrialVariant(queryClient, variant);

    return variant;
  } catch (error) {
    Sentry.captureException(error, { tags: { feature: 'trialVariant', phase: 'assign' } });
    return null;
  }
};
